import { c as _c } from "react/compiler-runtime";
import type { PlayInfo } from '@module/apis/client-side/live/type';
import ChatEmoticonSelectBox from '@module/components/ChatEmoticon/ChatEmoticonSelectBox';
import ChatEmoticonSelectPreview from '@module/components/ChatEmoticon/ChatEmoticonSelectPreview';
import { ChatTextArea } from '@module/components/ChatTextArea/ChatTextArea';
import SettingConfig from '@module/config/setting';
import useGetLoginInfoQuery from '@module/helpers/auth/useGetLoginInfoQuery';
import useChatComposer from '@module/helpers/chat/useChatComposer';
import { aspCond } from '@module/helpers/conditions/condition';
import useChatPanelStore from '@module/stores/useChatPanelStore';
import useModalStore from '@module/stores/useModalStore';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import ChatFontSizeButtons from '../../_components/ChatFontSizeButtons';
import SendChatToolBox from '../../_components/SendChatToolBox';
import ScrollDownButton from './ScrollDownButton';
interface Props {
  playInfo: PlayInfo;
  isRoomMaster: boolean;
  chatInputValue: string;
  setChatInputValue: Dispatch<SetStateAction<string>>;
  scrollRef: RefObject<HTMLDivElement | null>;
  isNearBottom: boolean;
}

/**
 * 채팅 입력/전송(쓰기 관심사). 입력창·이모티콘·폰트·전송 게이팅을 담당한다.
 * 메시지 리스트(읽기)는 형제 컴포넌트 ChatMessageList가 가진다.
 */
function ChatComposer(t0) {
  const $ = _c(67);
  const {
    playInfo,
    isRoomMaster,
    chatInputValue,
    setChatInputValue,
    scrollRef,
    isNearBottom
  } = t0;
  const {
    isLogin,
    isAuthDone
  } = useGetLoginInfoQuery();
  const {
    pushModal
  } = useModalStore();
  let t1;
  if ($[0] !== pushModal) {
    t1 = message => {
      pushModal({
        type: "CONFIRM_MODAL",
        options: {
          description: message
        }
      });
    };
    $[0] = pushModal;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== chatInputValue || $[3] !== setChatInputValue || $[4] !== t1) {
    t2 = {
      chatInputValue,
      setChatInputValue,
      onFreezeOnError: t1
    };
    $[2] = chatInputValue;
    $[3] = setChatInputValue;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    chatInputRef,
    handleChatInputKeyDown,
    markTypingStart,
    handleSubmitChat
  } = useChatComposer(t2);
  let t3;
  if ($[6] !== chatInputRef || $[7] !== isAuthDone || $[8] !== isLogin || $[9] !== pushModal) {
    t3 = () => {
      if (isLogin && isAuthDone) {
        return true;
      }
      chatInputRef.current?.blur();
      if (!isLogin) {
        if (aspCond.if("new-asp")) {
          SettingConfig.aspUtils?.goLogin();
        } else {
          pushModal({
            type: "AUTH_MODAL"
          });
        }
      } else {
        pushModal({
          type: "IDENTITY_VERIFICATION_MODAL"
        });
      }
      return false;
    };
    $[6] = chatInputRef;
    $[7] = isAuthDone;
    $[8] = isLogin;
    $[9] = pushModal;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const ensureChatAuth = t3;
  const {
    setPcFontSize,
    setIsChatEmoticonSelectBoxOpen,
    isChatEmoticonSelectBoxOpen,
    selectedBlockEmoticon,
    setSelectedBlockEmoticon
  } = useChatPanelStore(_temp);
  let t4;
  if ($[11] !== isAuthDone || $[12] !== isLogin) {
    t4 = resolveChatInputCopy({
      isLogin,
      isAuthDone
    });
    $[11] = isAuthDone;
    $[12] = isLogin;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const {
    placeholder,
    sendButtonLabel
  } = t4;
  let t5;
  if ($[14] !== chatInputRef || $[15] !== isLogin) {
    t5 = () => {
      if (isLogin) {
        return;
      }
      chatInputRef.current?.blur();
    };
    $[14] = chatInputRef;
    $[15] = isLogin;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== isNearBottom || $[18] !== scrollRef) {
    t6 = !isNearBottom && <ScrollDownButton onClick={() => {
      if (!scrollRef.current) {
        return;
      }
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }} />;
    $[17] = isNearBottom;
    $[18] = scrollRef;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== selectedBlockEmoticon || $[21] !== setSelectedBlockEmoticon) {
    t7 = selectedBlockEmoticon && <ChatEmoticonSelectPreview onClose={() => {
      setSelectedBlockEmoticon(null);
    }} selectedBlockEmoticon={selectedBlockEmoticon} />;
    $[20] = selectedBlockEmoticon;
    $[21] = setSelectedBlockEmoticon;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== setPcFontSize) {
    t8 = <ChatFontSizeButtons setPcFontSize={setPcFontSize} />;
    $[23] = setPcFontSize;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== isRoomMaster || $[26] !== playInfo) {
    t9 = playInfo.media.code && <SendChatToolBox playInfo={playInfo} isRoomMaster={isRoomMaster} mediaCode={playInfo.media.code} />;
    $[25] = isRoomMaster;
    $[26] = playInfo;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t8 || $[29] !== t9) {
    t10 = <div className="mb-[8px] flex w-full items-center justify-between border-0 bg-transparent text-[#dfe2ea]">{t8}{t9}</div>;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const t11 = chatInputRef as RefObject<HTMLTextAreaElement | null>;
  let t12;
  if ($[31] !== ensureChatAuth || $[32] !== handleSubmitChat) {
    t12 = () => {
      if (ensureChatAuth()) {
        handleSubmitChat();
      }
    };
    $[31] = ensureChatAuth;
    $[32] = handleSubmitChat;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== ensureChatAuth || $[35] !== markTypingStart) {
    t13 = () => {
      if (ensureChatAuth()) {
        markTypingStart();
      }
    };
    $[34] = ensureChatAuth;
    $[35] = markTypingStart;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== isAuthDone || $[38] !== isChatEmoticonSelectBoxOpen || $[39] !== isLogin || $[40] !== setIsChatEmoticonSelectBoxOpen) {
    t14 = isLogin && isAuthDone && !aspCond.if("asp") && !SettingConfig.isProd ? () => {
      setIsChatEmoticonSelectBoxOpen(!isChatEmoticonSelectBoxOpen);
    } : undefined;
    $[37] = isAuthDone;
    $[38] = isChatEmoticonSelectBoxOpen;
    $[39] = isLogin;
    $[40] = setIsChatEmoticonSelectBoxOpen;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== chatInputValue || $[43] !== handleChatInputKeyDown || $[44] !== placeholder || $[45] !== sendButtonLabel || $[46] !== setChatInputValue || $[47] !== t11 || $[48] !== t12 || $[49] !== t13 || $[50] !== t14) {
    t15 = <ChatTextArea ref={t11} value={chatInputValue} onValueChange={setChatInputValue} onSubmit={t12} placeholder={placeholder} onClick={t13} onKeyDown={handleChatInputKeyDown} minRows={1} maxRows={2} onEmojiClick={t14} sendButtonElem={sendButtonLabel} />;
    $[42] = chatInputValue;
    $[43] = handleChatInputKeyDown;
    $[44] = placeholder;
    $[45] = sendButtonLabel;
    $[46] = setChatInputValue;
    $[47] = t11;
    $[48] = t12;
    $[49] = t13;
    $[50] = t14;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  let t16;
  if ($[52] !== t10 || $[53] !== t15 || $[54] !== t5 || $[55] !== t6 || $[56] !== t7) {
    t16 = <div className="relative w-full border-t p-[12px]" onClick={t5}>{t6}{t7}{t10}{t15}</div>;
    $[52] = t10;
    $[53] = t15;
    $[54] = t5;
    $[55] = t6;
    $[56] = t7;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  let t17;
  if ($[58] !== chatInputRef || $[59] !== isChatEmoticonSelectBoxOpen || $[60] !== playInfo.media.userIdx || $[61] !== setIsChatEmoticonSelectBoxOpen || $[62] !== setSelectedBlockEmoticon) {
    t17 = !aspCond.if("asp") && !SettingConfig.isProd && isChatEmoticonSelectBoxOpen && <ChatEmoticonSelectBox onClose={() => {
      setIsChatEmoticonSelectBoxOpen(false);
    }} userIdx={playInfo.media.userIdx} onSelect={item => {
      setSelectedBlockEmoticon(item);
      chatInputRef.current?.focus();
    }} />;
    $[58] = chatInputRef;
    $[59] = isChatEmoticonSelectBoxOpen;
    $[60] = playInfo.media.userIdx;
    $[61] = setIsChatEmoticonSelectBoxOpen;
    $[62] = setSelectedBlockEmoticon;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  let t18;
  if ($[64] !== t16 || $[65] !== t17) {
    t18 = <>{t16}{t17}</>;
    $[64] = t16;
    $[65] = t17;
    $[66] = t18;
  } else {
    t18 = $[66];
  }
  return t18;
}
function _temp(state) {
  return {
    setPcFontSize: state.setPcFontSize,
    setIsChatEmoticonSelectBoxOpen: state.setIsChatEmoticonSelectBoxOpen,
    isChatEmoticonSelectBoxOpen: state.isChatEmoticonSelectBoxOpen,
    selectedBlockEmoticon: state.selectedBlockEmoticon,
    setSelectedBlockEmoticon: state.setSelectedBlockEmoticon
  };
}
export default ChatComposer;
function resolveChatInputCopy({
  isLogin,
  isAuthDone
}: {
  isLogin: boolean;
  isAuthDone: boolean;
}): {
  placeholder: string;
  sendButtonLabel: string | undefined;
} {
  if (!isLogin) return {
    placeholder: '로그인 후 BJ에게 인사하기',
    sendButtonLabel: '로그인'
  };
  if (!isAuthDone) return {
    placeholder: '본인인증 후 채팅 참여하기',
    sendButtonLabel: '인증하기'
  };
  return {
    placeholder: '채팅하기',
    sendButtonLabel: undefined
  };
}